import React, { useEffect, useRef } from "react";
import { MathfieldElement } from "mathlive";
import "./MathKeyboardInput.css";
import { PASTE_COMMAND } from "lexical";
import { $insertDataTransferForRichText } from "@lexical/clipboard";
import {
  $getRoot,
  $createParagraphNode,
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  $getNodeByKey,
  $insertNodes,
} from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import useBranding from "../../hooks/useBranding";

if (!customElements.get("math-field")) {
  customElements.define("math-field", MathfieldElement);
}

// Shown inside the Beta keyboard's math field while it holds no expression.
// LaTeX, so plain words have to be wrapped in \text{}.
const CONTENT_PLACEHOLDER = "\\text{Type your answer here}";

const MathKeyboardInput = ({ value, onChange, assignmentType }) => {
  const mathFieldRef = useRef(null);
  const lastKeyRef = useRef(null);
  const menuConfiguredRef = useRef(false);
  const [editor] = useLexicalComposerContext();
  const globalState = useSelector((state) => state.globalState);
  const userRole = globalState?.role;
  const branding = useBranding();
  // Paste is allowed only for an SRDS Extended-Question editor — same rule as the
  // answer field (MinimalEditor). Everywhere else (incl. ProSchool EQ) it's blocked
  // for students, and the keyboard's paste icon is hidden.
  const mathPasteBlocked =
    userRole === "Student" &&
    !(assignmentType === "EQ" && branding?.variant === "srds");

  // ─── Empty-field placeholder ────────────────────────────────────────────────
  // Set imperatively, not as a JSX attribute. React 19 assigns a matching
  // *property* on a custom element instead of an attribute, and MathfieldElement's
  // `placeholder` setter is a no-op until its internal mathfield exists — which it
  // does not when React applies props. The attribute is what MathLive reads
  // (`getOptionsFromAttributes`, and `attributeChangedCallback` once initialised),
  // so setting it here covers both an already-upgraded and a pending element.
  useEffect(() => {
    const mathField = mathFieldRef.current;
    if (!mathField) return;

    try {
      mathField.setAttribute("placeholder", CONTENT_PLACEHOLDER);
    } catch (e) {
      console.warn("MathKeyboardInput: placeholder not applied –", e);
    }

    // Same guard the options block below uses: if the element was still
    // upgrading, the attribute was recorded but not yet turned into an option.
    const timerId = setTimeout(() => {
      try {
        mathField.placeholder = CONTENT_PLACEHOLDER;
      } catch (e) {
        console.warn("MathKeyboardInput: placeholder not applied –", e);
      }
    }, 200);

    return () => clearTimeout(timerId);
  }, []);

  // ─── Menu strip (cut / copy / paste) ───────────────────────────────────────
  useEffect(() => {
    const mathField = mathFieldRef.current;
    if (!mathField || menuConfiguredRef.current) return;

    const timer = setTimeout(() => {
      try {
        const defaultItems = mathField.menuItems;
        if (Array.isArray(defaultItems)) {
          const itemsToRemove = ["paste", "cut", "copy"];
          mathField.menuItems = defaultItems.filter(
            (item) => !itemsToRemove.includes(item.id),
          );
          menuConfiguredRef.current = true;
        }
      } catch (e) {}
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  // ─── Hide virtual-keyboard clipboard actions for blocked students ───────────
  // The MathLive virtual keyboard's edit toolbar has a paste (and cut/copy)
  // keycap that reads the clipboard via its own command — it does NOT fire the
  // `paste` DOM event, so the handlers below can't catch it. We flag the body so
  // the scoped CSS in MathKeyboardInput.css hides those keycaps. Same gate the
  // paste blockers use: students on a non-EQ assignment.
  useEffect(() => {
    if (!mathPasteBlocked) return undefined;
    document.body.classList.add("math-paste-disabled");
    return () => document.body.classList.remove("math-paste-disabled");
  }, [mathPasteBlocked]);

  // ─── LaTeX normalisation ────────────────────────────────────────────────────
  // NOTE: wrapBracketText was intentionally NOT added here.
  // A previous attempt wrapped \left\lbrack…\right\rbrack content in \text{},
  // but setting mathField.value with the wrapped result triggered another
  // onInput event, causing a feedback loop that split each word into its own
  // separate bracket pair. The actual parse-error root cause was the \\lb
  // regex in MinimalEditor.js corrupting \lbrack → \lbracerack — fixed there.
  const normalizeLatexForMathlive = (latex) => {
    return latex
      .replace(/\\dots\b/g, "\\ldots")
      .replace(/\\rm\s*([a-zA-Z]+)/g, "\\mathrm{$1}")
      .replace(/\\rm\{([^}]*)\}/g, "\\mathrm{$1}");
  };

  // ─── Sync external value → mathField ───────────────────────────────────────
  useEffect(() => {
    const mathField = mathFieldRef.current;
    if (!mathField || value === undefined) return;

    let newValue = value || "";
    if (newValue.startsWith("$$") && newValue.endsWith("$$")) {
      newValue = newValue.slice(2, -2);
    }
    newValue = normalizeLatexForMathlive(newValue);

    try {
      mathField.value = newValue;
    } catch (e) {
      // Element not fully upgraded yet; value will be applied once ready.
    }
  }, [value]);

  // ─── Input handler ──────────────────────────────────────────────────────────
  const handleInput = (evt) => {
    if (lastKeyRef.current === "Enter") {
      lastKeyRef.current = null;
      return;
    }
    if (onChange) {
      onChange({ target: { value: `$$${evt.target.value}$$` } });
    }
  };

  // ─── Paste handling (Lexical + native MathField) ────────────────────────────
  useEffect(() => {
    const unregisterPasteCommand = editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        if (mathPasteBlocked) {
          event.preventDefault();
          toast.error("Pasting disabled");
          return true;
        } else {
          try {
            event.preventDefault();
            const clipboardData = event.clipboardData;
            if (!clipboardData) return false;

            const selection = $getSelection();
            if (!$isRangeSelection(selection)) return false;

            if (clipboardData.types.includes("text/html")) {
              $insertDataTransferForRichText(clipboardData, selection);
              return true;
            }

            const text = clipboardData.getData("text/plain");
            if (text) selection.insertText(text);
            return true;
          } catch (error) {
            return false;
          }
        }
      },
      1,
    );

    const mathField = mathFieldRef.current;
    if (mathField) {
      const handleMathFieldPaste = (e) => {
        if (mathPasteBlocked) {
          e.preventDefault();
          toast.error("Pasting disabled");
          return false;
        }
        return true;
      };

      mathField.addEventListener("paste", handleMathFieldPaste);
      return () => {
        unregisterPasteCommand();
        mathField.removeEventListener("paste", handleMathFieldPaste);
      };
    }

    return () => unregisterPasteCommand();
  }, [editor, assignmentType, mathPasteBlocked]);

  // ─── Keyboard shortcuts (Enter / Space) ────────────────────────────────────
  useEffect(() => {
    const mathField = mathFieldRef.current;
    if (!mathField) return;

    const handleKeyDown = (e) => {
      lastKeyRef.current = e.key;

      if (e.key === "Enter" && e.shiftKey) {
        e.preventDefault();
        try { mathField.insert("\\\\"); } catch (err) {}
      }

      if (e.key === " ") {
        e.preventDefault();
        try { mathField.insert("\\;"); } catch (err) {}
      }
    };

    mathField.addEventListener("keydown", handleKeyDown);
    return () => mathField.removeEventListener("keydown", handleKeyDown);
  }, [onChange]);

  // ─── MathLive options + virtual keyboard layout ─────────────────────────────
  // FIX: The original code called setOptions() synchronously on mount, before
  // the <math-field> custom element had finished upgrading. At that point the
  // element is a plain HTMLElement with no MathLive prototype, so
  // mathField.setOptions is undefined and accessing .options inside it throws:
  //   TypeError: Cannot read properties of undefined (reading 'options')
  //
  // Fix: defer with setTimeout(200) so the upgrade completes first, guard that
  // setOptions is actually a function before calling it, and auto-retry once
  // if the element still isn't ready.
  useEffect(() => {
    let timerId;

    const initOptions = () => {
      const mathField = mathFieldRef.current;

      // Guard: element must exist AND be a proper MathfieldElement instance.
      if (!mathField || typeof mathField.setOptions !== "function") {
        // Element not upgraded yet — retry once after a short delay.
        timerId = setTimeout(initOptions, 150);
        return;
      }

      try {
        mathField.setOptions({
          mathModeSpace: "\\;",
          spaceBehavior: "space",
          locale: "en",
        });

        mathField.mathVirtualKeyboardPolicy = "manual";

        if (window.mathVirtualKeyboard) {
          window.mathVirtualKeyboard.layouts = [
            "default",
            "symbols",

            // 🇬🇧 English
            {
              label: "English",
              rows: [
                ["q","w","e","r","t","y","u","i","o","p"],
                ["a","s","d","f","g","h","j","k","l"],
                ["z","x","c","v","b","n","m"],
                ["+","-","=","\\sqrt{}","\\frac{}{}"],
              ],
            },

            // 🇪🇸 Spanish
            {
              label: "Español",
              rows: [
                ["q","w","e","r","t","y","u","i","o","p"],
                ["a","s","d","f","g","h","j","k","l","ñ"],
                ["z","x","c","v","b","n","m","á","é","í"],
                ["ó","ú","ü","+","-","=","\\sqrt{}","\\frac{}{}"],
              ],
            },

            // 🇫🇷 French (AZERTY style)
            {
              label: "Français",
              rows: [
                ["a","z","e","r","t","y","u","i","o","p"],
                ["q","s","d","f","g","h","j","k","l","m"],
                ["w","x","c","v","b","n","é","è","à"],
                ["ç","ù","+","-","=","\\sqrt{}","\\frac{}{}"],
              ],
            },

            // 🏛 Latin
            {
              label: "Latin",
              rows: [
                ["a","b","c","d","e","f","g","h","i","j"],
                ["k","l","m","n","o","p","q","r","s","t"],
                ["u","v","w","x","y","z"],
                ["+","-","=","\\sqrt{}","\\frac{}{}"],
              ],
            },

            // 🇸🇦 Arabic
            {
              label: "العربية",
              rows: [
                ["ض","ص","ث","ق","ف","غ","ع","ه","خ","ح"],
                ["ج","د","ش","س","ي","ب","ل","ا","ت","ن"],
                ["م","ك","ط","ئ","ء","ؤ","ر","لا","ى","ة"],
                ["+","-","=","\\sqrt{}","\\frac{}{}"],
              ],
            },
          ];
        }
      } catch (e) {
        console.warn("MathKeyboardInput: setOptions failed –", e);
      }
    };

    // Wait for custom-element upgrade to complete before configuring.
    timerId = setTimeout(initOptions, 200);
    return () => clearTimeout(timerId);
  }, []); // run once on mount

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="cortex-math-keyboard-container light-theme">
      <div className="math-input-wrapper">
        <math-field
          ref={mathFieldRef}
          onInput={handleInput}
          virtual-keyboard-mode="manual"
          math-mode-space="\\;"
          space-behavior="space"
          style={{
            maxHeight: "280px",
            fontSize: "18px",
            backgroundColor: "#eef9ff",
            padding: "24px 16px",
            borderRadius: "10px",
            "--color-virtual-keyboard-toggle": "#0068FF",
            "--color-menu-toggle": "#0068FF",
          }}
        ></math-field>
      </div>
    </div>
  );
};

export default MathKeyboardInput;